import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import {
  EMPTY_POLL_DRAFT,
  IntranetPollDraftFields,
  IntranetPollView,
  type IntranetPollDraft,
} from '@/products/intranet/components/intranet-poll';
import {
  useDeleteIntranetPoll,
  useIntranetPoll,
  useUpsertIntranetPoll,
} from '@/products/intranet/features/intranet/use-intranet-poll';

/** Editor for a saved entry. Voting stays off; options lock after the first vote. */
export function IntranetPollEditor({ slug }: { slug: string }) {
  const query = useIntranetPoll(slug);
  const save = useUpsertIntranetPoll(slug);
  const remove = useDeleteIntranetPoll(slug);
  const [draft, setDraft] = useState<IntranetPollDraft | null>(null);
  const poll = query.data?.poll ?? null;

  if (query.isPending) {
    return <p className="text-sm text-muted-foreground">Umfrage wird geladen …</p>;
  }
  if (poll?.locked) {
    return <IntranetPollView poll={poll} voting={false} />;
  }

  const value =
    draft ??
    (poll
      ? { question: poll.question, options: poll.options.map((option) => option.label) }
      : EMPTY_POLL_DRAFT);

  async function onSave() {
    const options = value.options.map((option) => option.trim()).filter(Boolean);
    try {
      await save.mutateAsync({ question: value.question.trim(), options });
      setDraft(null);
      toast.success('Umfrage gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function onRemove() {
    try {
      await remove.mutateAsync();
      setDraft(null);
      toast.success('Umfrage entfernt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Entfernen fehlgeschlagen.');
    }
  }

  return (
    <div className="space-y-3">
      <IntranetPollDraftFields draft={value} onChange={setDraft} />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={save.isPending}
          onClick={() => void onSave()}
        >
          Umfrage speichern
        </Button>
        {poll ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={remove.isPending}
            onClick={() => void onRemove()}
          >
            Umfrage entfernen
          </Button>
        ) : null}
      </div>
    </div>
  );
}
